/**
 * Meta Pixel + Conversions API client helper. Web only.
 *
 * Dual-fire pattern (Meta's recommended setup for 2026):
 *   1. Fire `fbq('track', ...)` client-side for fast signal + browser cookies.
 *   2. POST the same event_id to our backend, which forwards to Meta's
 *      Conversions API (CAPI) server-side with hashed PII.
 *   3. Meta deduplicates by event_id so a single event counts once.
 *
 * Defends against iOS Safari ITP + ad blockers (~30% of users block
 * client-side pixels) by always firing server-side.
 *
 * Dormant mode: if EXPO_PUBLIC_META_PIXEL_ID is empty, every track call
 * no-ops (no fbq, no backend POST). Lets the code ship before the Pixel
 * is provisioned in Meta Business Manager.
 *
 * Note: Purchase event signature intentionally not exported here — wire
 * when cashout redemption flow exists (server-fires only).
 */

import { Platform } from 'react-native';

import { apiClient } from '@/services/api';

const PIXEL_ID = process.env.EXPO_PUBLIC_META_PIXEL_ID;
const CAPI_PATH = '/api/v1/conversions/meta';

type MetaEventName =
  | 'ViewContent'
  | 'Lead'
  | 'CompleteRegistration'
  | 'Purchase';

interface EventCommon {
  /** Strips `?r=<token>` referral params before sending; raw href otherwise. */
  eventSourceUrl?: string;
}

interface ViewContentParams extends EventCommon {
  dealSlug: string;
  brandName?: string;
}

interface LeadParams extends EventCommon {
  email?: string;
  phone?: string;
}

interface CompleteRegistrationParams extends EventCommon {
  userId: string;
  email?: string;
  phone?: string;
}

interface FbqWindow {
  fbq?: (
    method: string,
    eventName: string,
    params?: Record<string, unknown>,
    options?: { eventID?: string },
  ) => void;
}

function isEnabled(): boolean {
  return Platform.OS === 'web' && !!PIXEL_ID;
}

function newEventId(): string {
  // crypto.randomUUID is available in all browsers we support (Chrome 92+,
  // Safari 15.4+, Firefox 95+). Web-only gated so no RN polyfill concern.
  return globalThis.crypto.randomUUID();
}

function currentSourceUrl(): string | undefined {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
  const { origin, pathname } = window.location;
  // Drop query string — referral tokens (?r=...) are PII-adjacent.
  return `${origin}${pathname}`;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const value = document.cookie.match(
    new RegExp(`(?:^|; )${name}=([^;]*)`),
  )?.[1];
  if (value === undefined) return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    // Malformed %-sequence (third-party script wrote a raw % into the
    // cookie) — a thrown URIError here would crash the calling effect.
    return value;
  }
}

/**
 * Browser identifiers Meta matches on. `_fbp`/`_fbc` cookies are written
 * by the Pixel script; when an ad blocker stops the script, `_fbc` never
 * gets written even though the user arrived from an ad, so fall back to
 * building it from the `fbclid` query param in the same format the Pixel
 * would: `fb.1.<ms-timestamp>.<fbclid>`.
 */
function fbBrowserIds(): { fbp?: string; fbc?: string } {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return {};
  const fbp = readCookie('_fbp');
  let fbc = readCookie('_fbc');
  if (!fbc) {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  }
  return { fbp, fbc };
}

interface FireArgs {
  eventName: MetaEventName;
  eventId: string;
  fbqParams: Record<string, unknown>;
  capiPayload: Record<string, unknown>;
}

function fire({ eventName, eventId, fbqParams, capiPayload }: FireArgs): void {
  // Client-side fbq. Skips silently if Pixel script hasn't loaded yet
  // (ad blocker, CSP block, slow network). Backend CAPI still fires.
  const fbq = (window as unknown as FbqWindow).fbq;
  if (fbq) {
    try {
      fbq('track', eventName, fbqParams, { eventID: eventId });
    } catch (err) {
      if (__DEV__) console.warn('[metaPixel] fbq threw', err);
    }
  }

  // Server-side CAPI via our backend. Fire-and-forget — analytics
  // failures never block UX.
  void apiClient
    .post(CAPI_PATH, capiPayload, { skipAuth: true })
    .catch((err) => {
      if (__DEV__) console.warn('[metaPixel] CAPI POST failed', err);
    });
}

export function trackViewContent(params: ViewContentParams): void {
  if (!isEnabled()) return;
  const eventId = newEventId();
  const eventSourceUrl = params.eventSourceUrl ?? currentSourceUrl();

  fire({
    eventName: 'ViewContent',
    eventId,
    fbqParams: {
      content_name: params.dealSlug,
      content_category: params.brandName,
    },
    capiPayload: {
      event_name: 'ViewContent',
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: eventSourceUrl,
      ...fbBrowserIds(),
      params: {
        content_name: params.dealSlug,
        content_category: params.brandName,
      },
    },
  });
}

export function trackLead(params: LeadParams): void {
  if (!isEnabled()) return;
  const eventId = newEventId();
  const eventSourceUrl = params.eventSourceUrl ?? currentSourceUrl();

  fire({
    eventName: 'Lead',
    eventId,
    // Don't send raw PII to fbq — Meta's client-side AAM
    // (Automatic Advanced Matching) handles email/phone server-side
    // via CAPI hashed user_data instead.
    fbqParams: {},
    capiPayload: {
      event_name: 'Lead',
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: eventSourceUrl,
      ...fbBrowserIds(),
      email: params.email,
      phone: params.phone,
    },
  });
}

export function trackCompleteRegistration(
  params: CompleteRegistrationParams,
): void {
  if (!isEnabled()) return;
  const eventId = newEventId();
  const eventSourceUrl = params.eventSourceUrl ?? currentSourceUrl();

  fire({
    eventName: 'CompleteRegistration',
    eventId,
    fbqParams: {},
    capiPayload: {
      event_name: 'CompleteRegistration',
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: eventSourceUrl,
      ...fbBrowserIds(),
      user_id: params.userId,
      email: params.email,
      phone: params.phone,
    },
  });
}

// Exported for tests.
export const __test = {
  isEnabled,
  newEventId,
  currentSourceUrl,
  fbBrowserIds,
};
